import { FormikHelpers as FormikActions } from 'formik';
import * as React from 'react';

import { TwsAdminApi } from '@weddingspot/ws-api-client';
import { TwsAdmin, AmenitiesFormData } from '../../components/venue-management/types';
import { defaultAmenitiesFromData } from '../../components/venue-management/constants';
import { Amenities } from '../../components/venue-management/Amenities';
import { AmenityType } from '../../components/venue-management/AmenitiesCard';
import { WrappedWsApiValidationError, toFormikError, WrappedWsApiMessageError, WrappedWsApiError } from '../../utils/formik';
import AppLogger from '../../utils/logging';
const logger = AppLogger.createLogger();

interface OwnProps {
    currentVenue: TwsAdmin.AvailableVenue;

    // Notify parent that an initialization error occurred
    onInitError: () => void;

    // Notify parent that a form error occurred
    onFormError: () => void;

    // Notify parent that form was successfully submitted
    onFormSuccess: () => void;

    // Notify the partne that the form was touched
    setTouched: (touched: boolean) => void;
}

interface OwnState {
    choicesLoaded: boolean | null;
    choices: TwsAdmin.AmenitiesFormChoices | null;
    initialValuesLoaded: boolean | null;
    initialValues: AmenitiesFormData | null;
    extraAmenities: TwsAdmin.ExtraAmenity[];
    extraRestrictions: TwsAdmin.ExtraAmenity[];
}

export default class AmenitiesContainer extends React.Component<OwnProps, OwnState> {
    constructor(props: OwnProps) {
        super(props);

        this.state = {
            choicesLoaded: null,
            choices: null,
            initialValuesLoaded: null,
            initialValues: null,
            extraAmenities: [],
            extraRestrictions: [],
        };

        this.onSubmit = this.onSubmit.bind(this);
        this.handleCreateExtraAmenity = this.handleCreateExtraAmenity.bind(this);
        this.handleDeleteExtraAmenity = this.handleDeleteExtraAmenity.bind(this);
    }

    componentDidMount() {
        // Fetch amenities choices
        const venueId = this.props.currentVenue.venue_id;
        TwsAdminApi.getAmenitiesChoices()
            .then((choices) => {
                this.setState({
                    choices: choices,
                    choicesLoaded: true,
                });

                TwsAdminApi.getAmenitiesValues(venueId)
                    .then((values) => {
                        this.setState({
                            initialValues: values,
                            initialValuesLoaded: true,
                            extraAmenities: values.extra_amenities,
                            extraRestrictions: values.extra_restrictions,
                        });
                    })
                    .catch(() => {
                        this.setState({ initialValuesLoaded: false });
                    });
            })
            .catch(() => {
                this.setState({
                    choicesLoaded: false,
                    initialValuesLoaded: false,
                });
            });
    }

    render() {
        const { currentVenue } = this.props;

        return (
            <div>
                <Amenities
                    currentVenue={currentVenue}
                    choicesLoaded={this.state.choicesLoaded}
                    choices={this.state.choices}
                    valuesLoaded={this.state.initialValuesLoaded}
                    initialValues={this.state.initialValuesLoaded ? this.state.initialValues : defaultAmenitiesFromData}
                    onSubmit={this.onSubmit}
                    onInitError={() => alert('onInitError')}
                    onFormError={() => alert('onFormError')}
                    onFormSuccess={() => alert('onFormSuccess')}
                    setTouched={this.props.setTouched}
                    extraAmenities={this.state.extraAmenities}
                    extraRestrictions={this.state.extraRestrictions}
                    handleCreateExtraAmenity={this.handleCreateExtraAmenity}
                    handleDeleteExtraAmenity={this.handleDeleteExtraAmenity}
                />
            </div>
        );
    }

    private onSubmit(data: AmenitiesFormData, actions: FormikActions<AmenitiesFormData>) {
        actions.setSubmitting(true);
        actions.setStatus(undefined);

        // Pop out any tmp field values
        let cleaned = {};
        Object.keys(data)
            .filter((k) => !k.startsWith('__'))
            .map((k) => (cleaned[k] = data[k]));

        TwsAdminApi.updateAmenitiesValues(this.props.currentVenue.venue_id, cleaned as AmenitiesFormData)
            .then((res: TwsAdmin.AmenitiesReadData) => {
                this.props.onFormSuccess();
                this.setState({ initialValues: res });
            })
            .catch((err: WrappedWsApiError<TwsAdmin.AmenitiesWriteData>) => {
                // Handle proper API responses
                if (!!err.detail && !!err.status) {
                    if (err.status === 400) {
                        const validationError: WrappedWsApiValidationError<TwsAdmin.GeneralInfoWriteData> = err;

                        let transformed = toFormikError<TwsAdmin.AmenitiesWriteData>(validationError);

                        actions.setErrors(transformed);
                    } else {
                        actions.setStatus('An unknown error occurred while attemping' + ' to save values. Please contact support');
                    }
                } else {
                    actions.setStatus('An unknown error occurred while attemping' + ' to save values. Please contact support');
                }

                this.props.onFormError();
            })
            .then(() => {
                actions.setSubmitting(false);
            });

        logger.info(JSON.stringify(cleaned));

        return Promise.resolve();
    }

    private handleDeleteExtraAmenity(amenity: TwsAdmin.ExtraAmenity) {
        return TwsAdminApi.deleteExtraAmenities(this.props.currentVenue.venue_id, { extra_amenities: [amenity.value] })
            .then((res) => {
                this.props.onFormSuccess();
                const amenities = res.extra_amenities.filter((a) => a.amenity_type === AmenityType.AMENITY);
                const restrictions = res.extra_amenities.filter((a) => a.amenity_type === AmenityType.RESTRICTION);
                this.setState({
                    extraAmenities: amenities,
                    extraRestrictions: restrictions,
                });
            })
            .catch((err) => {
                // TODO: Make this more useful
                this.props.onFormError();
            });
    }

    private handleCreateExtraAmenity(name: string, amenityType: AmenityType) {
        return TwsAdminApi.createExtraAmenities(this.props.currentVenue.venue_id, {
            extra_amenities: [
                {
                    name: name,
                    amenity_type: amenityType,
                },
            ],
        })
            .then((res: TwsAdmin.ExtraAmenitiesReadData) => {
                this.props.onFormSuccess();
                const amenities = res.extra_amenities.filter((a) => a.amenity_type === AmenityType.AMENITY);
                const restrictions = res.extra_amenities.filter((a) => a.amenity_type === AmenityType.RESTRICTION);
                this.setState({
                    extraAmenities: amenities,
                    extraRestrictions: restrictions,
                });
            })
            .catch((err) => {
                // Handle proper API responses
                if (!!err.detail && !!err.status) {
                    if (err.status === 400) {
                        const validationError: WrappedWsApiValidationError<TwsAdmin.ExtraAmenitiesWriteData> = err;

                        let transformed = toFormikError<TwsAdmin.ExtraAmenitiesWriteData>(validationError);

                        throw transformed;
                    } else {
                        const msgError = err as WrappedWsApiMessageError;
                        throw msgError.detail;
                    }
                } else {
                    throw err;
                }
            });
    }
}
